import { useEffect, useId, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import { useGuardedNavigate } from '../../utils/leaveGuard';
import { ehrActionLabel, useEhrActionNeededCount } from '../../stores/ehrActionStore';
import { useWaitingTextCount, waitingTextLabel } from '../../pages/emr/ehrBadges';
import { useReadyReportCount, readyReportsBadgeText } from '../../pages/reports/readyReports';
import { useNavSections, useCurrentNav } from '../../navigation/useNavSections';
import type { NavGroupView, NavPageView, NavSectionView } from '../../navigation/navConfig';
import { useNavPins, type NavPins } from '../../navigation/navPins';
import { rememberNavEntry } from '../../navigation/navEntry';
import { useRovingFocus, type RovingItemProps } from './useRovingFocus';
import { isWideEditorRoute } from './wideEditorRoutes';
import { useSidebarReportFavorites } from './sidebarReportFavorites';
import './Sidebar.css';

/**
 * The sidebar, in two layers (Jeff, 2026-10-20).
 *
 * Left: the rail, one entry per SECTION (Home, Residents, Clinical, Menus, ...), each an icon with
 * its name under it, like the desktop program's module bar. It is always there, collapsed or not.
 * Right: the panel, the current section's pages in their groups, with the section's name on top.
 * Clicking a section on the rail opens its main page (Menus opens Menu Cycles), or the first page in
 * it the user can open. A section with one page has no panel. Collapsing hides the panel only, so
 * wide grids get the room and the rail still moves you between sections.
 *
 * What is in each section, and who may see it, comes from navigation/navConfig.ts, the list the
 * breadcrumbs, the phone More sheet and the top-bar search also read.
 *
 * As the tablet and phone drawer (`drawerOpen` set), a section on the rail opens its panel instead:
 * the drawer closes when the page changes, so opening Menus' main page hid Recycle Bin, Change
 * Requests and the rest until the drawer was opened again. A page in the panel opens it.
 *
 * Keyboard: the rail is one Tab stop and the panel another (useRovingFocus); the arrow keys move
 * between the entries inside each. Tabbing past the navigation into the page therefore costs two
 * stops, not one per section and page (the accessibility audit counts them on the meal pattern).
 */

/** Placeholder rows until the user's role loads: the real menu would flash features they do not have. */
function NavSkeleton() {
  return (
    <div aria-hidden="true" className="sidebar-nav-skeleton">
      {Array.from({ length: 7 }, (_, i) => (
        <div key={i} className="sidebar-skeleton-row" />
      ))}
    </div>
  );
}

/**
 * A plain left click navigates in place (asking a page with unsaved work first); anything else is a
 * normal link. `onFollow` runs just before an in-place navigation.
 */
function useLinkClick() {
  const navigate = useGuardedNavigate();
  return (route: string, onFollow?: () => void) => (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    onFollow?.();
    navigate(route);
  };
}

interface SidebarProps {
  /** Panel hidden: the rail only. */
  collapsed: boolean;
  /**
   * The page asks for the rail only (Resident Detail, Global Meal Patterns: useGlobalStore
   * sidebarForceCollapsed). Like a wide editor, the panel starts closed there and the toggle opens it
   * for that visit, without changing the saved setting. It used to flip the saved setting, which the
   * page overrode: the arrow did nothing there, and the panel stayed collapsed everywhere afterwards.
   */
  railOnly?: boolean;
  onToggle: () => void;
  onRefresh: () => void;
  /** Set only when the sidebar is the tablet/phone drawer: whether it is open. */
  drawerOpen?: boolean;
}

export default function Sidebar({ collapsed, railOnly = false, onToggle, onRefresh, drawerOpen }: SidebarProps) {
  const navigate = useGuardedNavigate();
  const { sections, ready } = useNavSections();
  const current = useCurrentNav(sections);

  // Counts the rail and panel carry: EHR messages waiting on someone, and the user's ready reports.
  const ehrCount = useEhrActionNeededCount();
  // EHR text waiting for mapping: spoken with the Action Needed count only. With nothing held, a number
  // here read as messages stuck (ZZ Hammonton, 2026-10-26: 67 phrases sent but not applied, none held),
  // so that text is counted on the Mappings tab and the Map waiting text button instead.
  const waitingText = useWaitingTextCount()?.total ?? 0;
  // On Print Queue, where those reports are listed (Jeff, 2026-10-23), not on Reports.
  const reportsShown = sections.some((s) => s.pages.some((p) => p.route === '/reports/print-queue'));
  const readyReports = useReadyReportCount(reportsShown);
  const badges: Badges = {
    '/emr': ehrCount > 0
      ? { text: String(ehrCount), spoken: [ehrActionLabel(ehrCount), waitingText > 0 ? waitingTextLabel(waitingText) : ''].filter(Boolean).join(', '), alert: true }
      : null,
    '/reports/print-queue': readyReports > 0 ? { text: readyReportsBadgeText(readyReports), spoken: `${readyReports} ready` } : null,
  };

  // Pinned and Recent pages (navigation/navPins.ts). Every page opened is put at the front of
  // Recent; Home lists both under its own pages.
  const pins = useNavPins(sections);
  const { recordVisit } = pins;
  const visitedKey = current.page?.key ?? null;
  useEffect(() => { if (visitedKey) recordVisit(visitedKey); }, [visitedKey, recordVisit]);
  const pinGroups: NavGroupView[] = [{ title: 'Pinned', pages: pins.pinnedPages }, { title: 'Recent', pages: pins.recentPages }]
    .filter((g) => g.pages.length > 0);
  // A section with one page has no panel, unless it is Home with something pinned or recent.
  const hasPanel = (s: NavSectionView) => s.pages.length > 1 || (s.id === 'home' && pinGroups.length > 0);

  // In the drawer: the section whose panel a tap on the rail opened, until the drawer closes.
  const drawer = drawerOpen !== undefined;
  const [browsingId, setBrowsingId] = useState<string | null>(null);
  useEffect(() => { if (!drawerOpen) setBrowsingId(null); }, [drawerOpen]);
  const browsing = drawer ? sections.find((s) => s.id === browsingId) ?? null : null;

  const shownSection = browsing ?? current.section;
  const panelSection = shownSection && hasPanel(shownSection) ? shownSection : null;
  const homeGroups = panelSection?.id === 'home' ? pinGroups : [];
  const rail = useRovingFocus(sections.length, sections.findIndex((s) => s.id === current.sectionId));
  // On a wide editor, and on a page that asks for the rail only, the panel starts closed whatever the
  // saved setting; the toggle opens it for this visit only, without changing the saved setting.
  const { pathname } = useLocation();
  const closedHere = isWideEditorRoute(pathname) || railOnly;
  // The page the user reopened the panel on; any other page starts closed again.
  const [openedOn, setOpenedOn] = useState<string | null>(null);
  const openOnEditor = closedHere && openedOn === pathname;
  const panelClosed = closedHere ? !openOnEditor : collapsed;
  // A section opened from the drawer's rail shows its panel even on a wide editor.
  const showPanel = (!panelClosed || !!browsing) && !!panelSection;

  return (
    <aside
      data-tour="sidebar"
      role="navigation"
      aria-label="Main navigation"
      className={`sidebar${showPanel ? ' sidebar--with-panel' : ''}`}
      style={{ width: showPanel ? 'var(--left-sidebar-two-layer-width)' : 'var(--left-sidebar-collapsed-width)' }}
    >
      <div
        className={`sidebar-logo${showPanel ? '' : ' sidebar-logo--rail'}`}
        onClick={() => navigate('/dashboard')}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/dashboard'); } }}
        role="button"
        tabIndex={0}
        aria-label="Dietech home - go to dashboard"
      >
        <img src="/icons/DietechLogo.png" alt="" role="presentation" style={{ width: 32, height: 32 }} />
        {showPanel && <span className="sidebar-logo__name">Dietech</span>}
        {showPanel && (
          <Tooltip title="Refresh data">
            <IconButton
              size="small"
              aria-label="Refresh data"
              onClick={(e) => { e.stopPropagation(); onRefresh(); }}
              sx={{ ml: 'auto', color: 'var(--sidebar-text)', opacity: 0.6, '&:hover': { opacity: 1 } }}
            >
              <i className="bi bi-arrow-clockwise" style={{ fontSize: 'var(--font-size-base)' }} aria-hidden="true" />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className="sidebar-two-layer">
        <nav aria-label="Sections" className="sidebar-rail">
          {!ready ? (
            <NavSkeleton />
          ) : (
            <ul className="sidebar-rail__list" data-roving-group>
              {sections.map((s, i) => (
                <li key={s.id}>
                  <RailItem
                    section={s}
                    active={current.sectionId === s.id}
                    // A section opened from the drawer's rail is lit while its pages are listed.
                    lit={(browsing?.id ?? current.sectionId) === s.id}
                    shown={showPanel && panelSection?.id === s.id}
                    onOpenPanel={drawer && hasPanel(s) ? () => setBrowsingId(s.id) : undefined}
                    badges={badges}
                    roving={rail.itemProps(i)}
                  />
                </li>
              ))}
            </ul>
          )}
        </nav>
        {showPanel && panelSection && (panelSection.id === 'reports'
          ? <ReportsPanel section={panelSection} activeRoute={current.page?.route ?? null} pathname={pathname} badges={badges} pins={pins} />
          : (
            <SectionPanel
              section={panelSection}
              trailing={homeGroups}
              activeRoute={current.page?.route ?? null}
              badges={badges}
              pins={pins}
            />
          ))}
      </div>

      {panelSection && (
        <button
          type="button"
          className="sidebar-toggle"
          onClick={closedHere ? () => setOpenedOn(openOnEditor ? null : pathname) : onToggle}
          aria-label={showPanel ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          <i className={`bi ${showPanel ? 'bi-chevron-double-left' : 'bi-chevron-double-right'}`} style={{ fontSize: 'var(--icon-size)' }} aria-hidden="true" />
        </button>
      )}
    </aside>
  );
}

interface Badge { text: string; spoken: string; alert?: boolean }
type Badges = Record<string, Badge | null>;

/** The badge a section carries on the rail: the counts of the pages in it. */
function sectionBadge(section: NavSectionView, badges: Badges) {
  for (const page of section.pages) {
    const b = !page.disabledReason ? badges[page.route] : null;
    if (b) return b;
  }
  return null;
}

function RailItem({ section, active, lit, shown, onOpenPanel, badges, roving }: {
  section: NavSectionView;
  /** The current page is in this section. */
  active: boolean;
  /** Drawn as the selected section. */
  lit: boolean;
  /** This section's panel is the one showing. */
  shown: boolean;
  /** In the drawer: a plain tap opens the section's panel rather than its main page. */
  onOpenPanel?: () => void;
  badges: Badges;
  roving: RovingItemProps;
}) {
  const onClick = useLinkClick();
  const badge = sectionBadge(section, badges);
  return (
    <a
      href={section.railRoute}
      onClick={onOpenPanel
        ? (e) => {
            if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            onOpenPanel();
          }
        : onClick(section.railRoute)}
      className={`sidebar-rail-item${lit ? ' active' : ''}`}
      aria-current={active ? 'true' : undefined}
      aria-expanded={onOpenPanel ? shown : undefined}
      {...roving}
    >
      <i className={`bi ${section.icon}`} aria-hidden="true" />
      <span className="sidebar-rail-item__label">{section.label}</span>
      {badge && (
        <>
          <span className={`sidebar-nav-badge sidebar-nav-badge--rail${badge.alert ? ' sidebar-nav-badge--alert' : ''}`} aria-hidden="true">{badge.text}</span>
          <span className="sr-only">, {badge.spoken}</span>
        </>
      )}
    </a>
  );
}

/**
 * The Reports panel: the user's starred reports in a Favorites group under the Reports group (Jeff,
 * 2026-10-22: "the reports queue should always be at the top and then favorite under it"),
 * then the section's pages. The favorites come from the Reports page's own source
 * (useReportCatalog: the same queries and cache, the same rules for what the user may open), so
 * starring on the page shows here at once. One line per report, its name; it opens the report's
 * form, as its card does. No group when there are none; while the list loads, the one last shown
 * (sidebarReportFavorites.ts), so the panel does not jump.
 */
function ReportsPanel({ section, activeRoute, pathname, badges, pins }: {
  section: NavSectionView;
  activeRoute: string | null;
  pathname: string;
  badges: Badges;
  pins: NavPins;
}) {
  const favorites = useSidebarReportFavorites();
  // On a favorite's form, that favorite is the current entry, not Reports.
  const onFavorite = favorites?.pages.some((p) => p.route === pathname) ? pathname : null;
  return <SectionPanel section={section} leading={favorites} activeRoute={onFavorite ?? activeRoute} badges={badges} pins={pins} />;
}

/**
 * Whether a scrolling box has more above or below what shows. The panel can be taller than the
 * window (Purchasing with every feature on, at 900px), and nothing said it scrolled: the last
 * entries were simply cut off (UI audit, 2026-10-25). The frame draws a fade and a chevron on the
 * side that has more.
 */
function useScrollCue(ref: RefObject<HTMLElement | null>, contentKey: string): { above: boolean; below: boolean } {
  const [cue, setCue] = useState({ above: false, below: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const above = el.scrollTop > 1;
      const below = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setCue((c) => (c.above === above && c.below === below ? c : { above, below }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    for (const child of Array.from(el.children)) ro?.observe(child);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      ro?.disconnect();
    };
  }, [ref, contentKey]);
  return cue;
}

function SectionPanel({ section, leading = null, trailing = [], activeRoute, badges, pins }: {
  section: NavSectionView;
  /** A group drawn after the section's first group (Reports: Favorites under Reports / Print Queue /
   *  Scheduled); it shares the panel's Tab stop. */
  leading?: NavGroupView | null;
  /** Groups drawn after the section's own (Home: Pinned, Recent); they share the Tab stop too. */
  trailing?: NavGroupView[];
  activeRoute: string | null;
  badges: Badges;
  pins: NavPins;
}) {
  const baseId = useId();
  const [firstGroup, ...restGroups] = section.groups;
  const groups = [...(firstGroup ? [firstGroup] : []), ...(leading ? [leading] : []), ...restGroups, ...trailing];
  // Said after P pins or unpins a page from the keyboard, where no button changed under focus.
  const [said, setSaid] = useState('');
  // Indexes run across the groups, in the order the links are drawn.
  const order = groups.flatMap((g) => g.pages.map((p) => p.route));
  const roving = useRovingFocus(order.length, activeRoute ? order.indexOf(activeRoute) : 0);
  const scrollRef = useRef<HTMLElement>(null);
  const cue = useScrollCue(scrollRef, `${section.id}:${order.join('|')}`);
  return (
    <div className={`sidebar-panel-frame${cue.above ? ' sidebar-panel-frame--more-above' : ''}${cue.below ? ' sidebar-panel-frame--more-below' : ''}`}>
      <nav ref={scrollRef} aria-label={section.label} className="sidebar-panel" data-roving-group>
        <h2 className="sidebar-section-title">{section.label}</h2>
        {groups.map((group, gi) => {
          const headingId = `${baseId}-g${gi}`;
          return (
            <div key={group.title || gi} className="sidebar-panel__group">
              {group.title && <h3 id={headingId} className="sidebar-panel__group-label">{group.title}</h3>}
              <ul className="sidebar-panel__list" aria-labelledby={group.title ? headingId : undefined}>
                {group.pages.map((page) => (
                  <li key={page.route}>
                    <PanelLink
                      page={page}
                      sectionId={section.id}
                      active={page.route === activeRoute}
                      badge={badges[page.route] ?? null}
                      roving={roving.itemProps(order.indexOf(page.route))}
                      pin={pins.canPin(page) ? {
                        pinned: pins.isPinned(page.key),
                        // In Home's Pinned group the star unpins and the line goes with it.
                        shown: group.title === 'Pinned' && section.id === 'home',
                        toggle: (fromKey) => {
                          const wasPinned = pins.isPinned(page.key);
                          pins.togglePin(page.key);
                          if (fromKey) setSaid(`${wasPinned ? 'Unpinned' : 'Pinned'} ${page.label}`);
                        },
                      } : null}
                    />
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        <div className="sr-only" role="status" aria-live="polite">{said}</div>
      </nav>
      <i className="bi bi-chevron-down sidebar-panel-cue" aria-hidden="true" />
    </div>
  );
}

interface PinControl {
  pinned: boolean;
  /** Always visible (Home's Pinned group); elsewhere the star shows on hover and focus. */
  shown: boolean;
  /** `fromKey`: toggled with P on the link, so the change is announced. */
  toggle: (fromKey: boolean) => void;
}

/** The panel entries, in drawing order, inside the panel that holds `el`. */
function panelEntries(el: HTMLElement): HTMLElement[] {
  const group = el.closest('[data-roving-group]');
  return group ? Array.from(group.querySelectorAll<HTMLElement>('[data-roving]')) : [];
}

/**
 * Pins or unpins a page (Jeff, 2026-10-22). A real button with aria-pressed, named "Pin <page>" or
 * "Unpin <page>", but not a Tab stop of its own: the panel stays ONE stop. From the keyboard, Right
 * Arrow on the page's link moves to its star (Left Arrow or Escape goes back), or P on the link
 * pins it directly; a screen reader's browse mode finds the button beside the link as usual.
 */
function PinButton({ label, pin }: { label: string; pin: PinControl }) {
  const ref = useRef<HTMLButtonElement>(null);
  const link = () => ref.current?.parentElement?.querySelector<HTMLElement>('[data-roving]') ?? null;
  const press = () => {
    // Unpinning in Home's Pinned group removes the line under focus: hand focus to a neighbour.
    if (pin.shown && pin.pinned) {
      const own = link();
      const entries = own ? panelEntries(own) : [];
      const at = own ? entries.indexOf(own) : -1;
      const next = entries[at + 1] ?? entries[at - 1] ?? null;
      pin.toggle(false);
      requestAnimationFrame(() => next?.focus());
      return;
    }
    pin.toggle(false);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowLeft' || e.key === 'Escape') {
      e.preventDefault();
      link()?.focus();
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Home' || e.key === 'End') {
      // Up and down move between the panel's entries, as they do from the link.
      e.preventDefault();
      const own = link();
      if (!own) return;
      own.focus();
      own.dispatchEvent(new window.KeyboardEvent('keydown', { key: e.key, bubbles: true }));
    }
  };
  return (
    <button
      ref={ref}
      type="button"
      className={`sidebar-pin${pin.pinned ? ' sidebar-pin--on' : ''}${pin.shown ? ' sidebar-pin--shown' : ''}`}
      aria-pressed={pin.pinned}
      aria-label={`${pin.pinned ? 'Unpin' : 'Pin'} ${label}`}
      title={pin.pinned ? 'Unpin from Home' : 'Pin to Home'}
      tabIndex={-1}
      onClick={press}
      onKeyDown={onKeyDown}
    >
      <i className={`bi ${pin.pinned ? 'bi-star-fill' : 'bi-star'}`} aria-hidden="true" />
    </button>
  );
}

function PanelLink({ page, sectionId, active, badge, roving, pin = null }: {
  page: NavPageView;
  /** The panel's section: a page listed in two sections stays in the one it was opened from. */
  sectionId: string;
  active: boolean;
  badge: Badge | null;
  roving: RovingItemProps;
  pin?: PinControl | null;
}) {
  const onClick = useLinkClick();
  const reasonId = useId();
  // Right Arrow reaches the star, P pins; everything else is the panel's roving keys.
  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (pin && !e.altKey && !e.ctrlKey && !e.metaKey) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        e.currentTarget.parentElement?.querySelector<HTMLElement>('.sidebar-pin')?.focus();
        return;
      }
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault();
        pin.toggle(true);
        return;
      }
    }
    roving.onKeyDown(e);
  };
  if (page.disabledReason) {
    // Greyed out with the reason, not hidden: people could not find a menu item that vanished.
    // While the user is on the page anyway (EMR opened by its address with EHR off), it is the
    // current page and looks it, as every other current page does; the reason still goes with it.
    return (
      <>
        <Tooltip title={`${page.label}: ${page.disabledReason}`} placement="right" arrow>
          {/* A link that cannot be followed: announced as "link, unavailable" with the reason. */}
          <span
            role="link"
            className={`sidebar-panel-link sidebar-panel-link--disabled${active ? ' active' : ''}`}
            aria-disabled="true"
            aria-current={active ? 'page' : undefined}
            aria-describedby={reasonId}
            {...roving}
          >
            {page.label}
          </span>
        </Tooltip>
        <span id={reasonId} hidden>{page.disabledReason}</span>
      </>
    );
  }
  const link = (
    <a
      href={page.route}
      onClick={onClick(page.route, () => rememberNavEntry(page.key, sectionId))}
      className={`sidebar-panel-link${active ? ' active' : ''}${pin ? ' sidebar-panel-link--pinnable' : ''}`}
      aria-current={active ? 'page' : undefined}
      aria-keyshortcuts={pin ? 'P' : undefined}
      {...roving}
      onKeyDown={onKeyDown}
    >
      <span className="sidebar-panel-link__label">{page.label}</span>
      {badge && (
        <>
          <span className={`sidebar-nav-badge${badge.alert ? ' sidebar-nav-badge--alert' : ''}`} aria-hidden="true">{badge.text}</span>
          <span className="sr-only">, {badge.spoken}</span>
        </>
      )}
    </a>
  );
  if (!pin) return link;
  return (
    <div className="sidebar-panel-row">
      {link}
      <PinButton label={page.label} pin={pin} />
    </div>
  );
}
